import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

export interface HotelManualInfo {
  id: string;
  hotel_id: string;
  review_text_es: string | null;
  review_text_pt: string | null;
  review_text_en: string | null;
  is_pet_friendly: boolean | null;
  photos: string[];
}

export function useHotelManualInfo(hotelId: string | number | null | undefined) {
  const [manualInfo, setManualInfo] = useState<HotelManualInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { i18n } = useTranslation();

  const fetchManualInfo = useCallback(async () => {
    if (!hotelId) {
      setManualInfo(null);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("hotel_manual_info")
        .select("id, hotel_id, review_text_es, review_text_pt, review_text_en, is_pet_friendly, photos")
        .eq("hotel_id", hotelId.toString())
        .maybeSingle();

      if (error) {
        console.error("Error fetching hotel manual info:", error);
        setManualInfo(null);
      } else if (data) {
        setManualInfo({
          id: data.id,
          hotel_id: data.hotel_id,
          review_text_es: data.review_text_es,
          review_text_pt: data.review_text_pt,
          review_text_en: data.review_text_en,
          is_pet_friendly: data.is_pet_friendly,
          photos: Array.isArray(data.photos) ? (data.photos as string[]) : [],
        });
      } else {
        setManualInfo(null);
      }
    } catch (err) {
      console.error("Error fetching hotel manual info:", err);
      setManualInfo(null);
    } finally {
      setIsLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    fetchManualInfo();
  }, [fetchManualInfo]);

  const refetch = useCallback(() => {
    fetchManualInfo();
  }, [fetchManualInfo]);

  // Helper to get the review text in the current language - no fallback to other languages
  const getLocalizedReviewText = useCallback((info: HotelManualInfo | null): string | null => {
    if (!info) return null;
    
    const lang = i18n.language;
    
    // Return only the text for the selected language, no fallbacks
    if (lang.startsWith('pt')) {
      return info.review_text_pt || null;
    } else if (lang.startsWith('en')) {
      return info.review_text_en || null;
    } else {
      // Default to Spanish
      return info.review_text_es || null;
    }
  }, [i18n.language]);

  return { manualInfo, isLoading, refetch, getLocalizedReviewText };
}
